import React, { useEffect, useState } from "react";
import { Button, Card, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { Controller, Resolver, useForm } from "react-hook-form";
import {
  IIncident,
  IIncidentCreate,
  IIncidentDetails,
  IIncidentActivity,
} from "../../../../shared/interfaces/incident/IIncident";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import BreadcrumbItem from "../../../../shared/components/BreadcrumbItem";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import { handleError } from "../../../../shared/functions/handleError";
import { WebIncidentService } from "../service";
import Select2 from "../../../../shared/components/Select2";
import GoogleMapComponent, {
  IMapMarker,
} from "../../../../shared/components/GoogleMapComponent";
import { instance } from "../../../../utils/api/api.service";
import DeleteModal from "../../../../shared/components/DeleteModal";
import { useIncidentItemWebSocket } from "../../../../shared/hooks/useWebSocket";
import { useNewActivityAlert } from "../../../../shared/hooks/useNewActivityAlert";
import ActivityIcon from "../../../../shared/components/ActivityIcon";
import { getActivityDisplay } from "../../../../shared/functions/incidentDisplay";
import {
  IRawActivityPayload,
  isActivityChangeSignal,
  prependActivity,
  sortActivitiesNewestFirst,
  toIncidentActivity,
} from "../../../../shared/functions/incidentActivity";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/store";

const incidentSchema = yup.object().shape({
  incidentType: yup.string().required("validation.required"),
  statusType: yup.string().required("validation.required"),
  drillMode: yup.boolean().required("validation.required"),
  longitude: yup
    .number()
    .required("validation.required")
    .min(-180, "validation.longitudeRange")
    .max(180, "validation.longitudeRange")
    .typeError("validation.required"),
  latitude: yup
    .number()
    .required("validation.required")
    .min(-90, "validation.latitudeRange")
    .max(90, "validation.latitudeRange")
    .typeError("validation.required"),
});

const initialValues: Partial<IIncident> = {
  incidentType: "active_shooter",
  statusType: "PENDING",
  drillMode: false,
  longitude: 0,
  latitude: 0,
};

const OperatorIncidentForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { incidentId } = useParams();
  const { userData } = useSelector((state: RootState) => state.auth);
  const [incident, setIncident] = useState<IIncident | null>(null);
  const [incidentDetails, setIncidentDetails] =
    useState<IIncidentDetails | null>(null);
  const [incidentTypes, setIncidentTypes] = useState<string[]>([]);
  const [statusTypes, setStatusTypes] = useState<string[]>([]);
  const [contentModal, setContentModal] = useState<{
    show: boolean;
    loading: boolean;
    url: string | null;
    mediaType: string | null;
  }>({ show: false, loading: false, url: null, mediaType: null });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteActivityModal, setShowDeleteActivityModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<
    IIncidentActivity | undefined
  >();

  const handleOpenContent = (activity: IIncidentActivity) => {
    const contentUrl = WebIncidentService.getActivityContent(activity.id);
    setContentModal({
      show: true,
      loading: true,
      url: null,
      mediaType: activity.mediaType || null,
    });

    instance
      .get(contentUrl, { responseType: "blob" })
      .then((response) => {
        const blobUrl = window.URL.createObjectURL(response.data);
        setContentModal((prev) => ({ ...prev, loading: false, url: blobUrl }));
      })
      .catch((error) => {
        handleError(error);
        setContentModal({
          show: false,
          loading: false,
          url: null,
          mediaType: null,
        });
      });
  };

  const handleCloseContent = () => {
    if (contentModal.url) {
      window.URL.revokeObjectURL(contentModal.url);
    }
    setContentModal({
      show: false,
      loading: false,
      url: null,
      mediaType: null,
    });
  };

  const handleDeleteIncident = () => {
    WebIncidentService.delete(Number(incidentId))
      .then(() => {
        navigate(-1);
        enqueueSnackbar(
          t("toasts.successfullyDeleted", {
            name: t(`incident.type.${incident?.incidentType}`),
          }),
          { variant: "success" },
        );
      })
      .catch(handleError);
  };

  const handleDeleteActivity = () => {
    if (!selectedActivity) return;
    WebIncidentService.deleteActivity(selectedActivity.id)
      .then(() => {
        setIncidentDetails((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            activities: prev.activities.filter(
              (a) => a.id !== selectedActivity.id,
            ),
          };
        });
        setShowDeleteActivityModal(false);
        setSelectedActivity(undefined);
        enqueueSnackbar(t("incident.activityDeleted"), { variant: "success" });
      })
      .catch(handleError);
  };

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm<IIncident>({
    resolver: yupResolver(incidentSchema) as unknown as Resolver<IIncident>,
    defaultValues: async () => {
      if (Boolean(incidentId)) {
        try {
          const response = await WebIncidentService.getSingle(Number(incidentId));
          setIncident(response?.data);
          return {
            ...response?.data,
          };
        } catch (e) {
          handleError(e);
          return initialValues as IIncident;
        }
      }
      return initialValues as IIncident;
    },
  });

  const incidentTypeOptions = incidentTypes.map((type) => ({
    value: type,
    label: t(`incident.type.${type}`),
  }));

  const statusTypeOptions = statusTypes.map((status) => ({
    value: status,
    label: t(`incident.statusType.${status}`),
  }));

  const selectedIncidentType = watch("incidentType");
  const selectedStatusType = watch("statusType");

  useEffect(() => {
    WebIncidentService.getTypes()
      .then((response) => {
        setIncidentTypes(response?.data || []);
      })
      .catch(handleError);

    WebIncidentService.getStatusTypes()
      .then((response) => {
        setStatusTypes(response?.data || []);
      })
      .catch(handleError);

    if (incidentId) {
      WebIncidentService.getDetails(Number(incidentId))
        .then((response) => {
          setIncidentDetails(response?.data || null);
        })
        .catch(handleError);
    }
  }, [incidentId]);

  const handleActivityReceived = (rawActivity: IRawActivityPayload) => {
    if (isActivityChangeSignal(rawActivity)) {
      WebIncidentService.getDetails(Number(incidentId))
        .then((response) => setIncidentDetails(response?.data || null))
        .catch(handleError);
      return;
    }
    const fallbackUserName =
      `${userData?.firstName ?? ""} ${userData?.lastName ?? ""}`.trim();
    const newActivity = toIncidentActivity(rawActivity, fallbackUserName);
    setIncidentDetails((prev) =>
      prev
        ? { ...prev, activities: prependActivity(prev.activities, newActivity) }
        : prev,
    );
  };

  const newActivityIds = useNewActivityAlert(incidentId, incidentDetails?.activities);

  useIncidentItemWebSocket(incidentId, handleActivityReceived);

  const onSubmit = async (data: IIncident) => {
    const isUpdate = !!data?.id;
    if (isUpdate) {
      await WebIncidentService.update(data, data?.id)
        .then(() => {
          navigate(-1);
          reset();
          enqueueSnackbar(
            t("toasts.successfullyUpdated", {
              name: t(`incident.type.${data?.incidentType}`),
            }),
            {
              variant: "success",
            },
          );
        })
        .catch(handleError);
    } else {
      const createData: IIncidentCreate = {
        incidentType: data.incidentType,
        drillMode: data.drillMode,
        longitude: data.longitude,
        latitude: data.latitude,
        beaconUuid: data.beaconUuid || "",
      };
      await WebIncidentService.create(createData)
        .then(() => {
          navigate(-1);
          reset();
          enqueueSnackbar(
            t("toasts.successfullyCreated", {
              name: t(`incident.type.${data?.incidentType}`),
            }),
            {
              variant: "success",
            },
          );
        })
        .catch(handleError);
    }
  };

  const isViewMode = !!incidentId;

  const formatActivityDate = (dateString?: string): string => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    return isNaN(d.getTime()) ? "-" : d.toLocaleString();
  };

  const activityMarkers: IMapMarker[] = (incidentDetails?.activities || [])
    .filter((a) => a.latitude && a.longitude)
    .map((a) => ({
      lat: a.latitude!,
      lng: a.longitude!,
      title: a.userFullName,
      note: getActivityDisplay(a, t, incidentDetails?.incident?.incidentType).message,
      subtitle: formatActivityDate(a.createdAt),
    }));

  return (
    <React.Fragment>
      <BreadcrumbItem
        mainTitle={t("incident.incidents")}
        subTitle={
          incidentId ? t("incident.viewIncident") : t("incident.createIncident")
        }
      />

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <Card.Header>
            <h5>{t("incident.incidentInformation")}</h5>
          </Card.Header>
          <Card.Body>
            <Row>
              {/* Create-mode fields */}
              {!isViewMode && (
                <>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="incidentType">
                      <Form.Label>{t("incident.incidentType")}</Form.Label>
                      <Select2<string>
                        name="incidentType"
                        placeholder={t("incident.selectIncidentType")}
                        options={incidentTypeOptions}
                        onChange={(value) =>
                          setValue("incidentType", value as string)
                        }
                        value={selectedIncidentType}
                      />
                      {errors.incidentType?.message && (
                        <div className="invalid-feedback d-block">
                          {t(errors.incidentType.message as string)}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="drillMode">
                      <Form.Label>{t("incident.drillMode")}</Form.Label>
                      <Controller
                        name="drillMode"
                        control={control}
                        render={({ field }) => (
                          <Form.Check
                            type="switch"
                            id="drillMode"
                            label={
                              field.value ? t("general.yes") : t("general.no")
                            }
                            checked={!!field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        )}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t("incident.selectLocation")}</Form.Label>
                      <GoogleMapComponent
                        latitude={watch("latitude") || 48.2082}
                        longitude={watch("longitude") || 16.3738}
                        height="280px"
                        onLocationSelect={(lat, lng) => {
                          setValue("latitude", lat);
                          setValue("longitude", lng);
                        }}
                      />
                      <small className="text-muted">
                        {t("incident.clickMapToSetLocation")}
                      </small>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="latitude">
                      <Form.Label>{t("general.latitude")}</Form.Label>
                      <Controller
                        name="latitude"
                        control={control}
                        render={({ field }) => (
                          <Form.Control
                            {...field}
                            type="number"
                            step="any"
                            isInvalid={!!errors.latitude?.message}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.latitude?.message &&
                          t(errors.latitude.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="longitude">
                      <Form.Label>{t("general.longitude")}</Form.Label>
                      <Controller
                        name="longitude"
                        control={control}
                        render={({ field }) => (
                          <Form.Control
                            {...field}
                            type="number"
                            step="any"
                            isInvalid={!!errors.longitude?.message}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.longitude?.message &&
                          t(errors.longitude.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </>
              )}

              {/* Edit-mode: status only */}
              {isViewMode && (
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="statusType">
                    <Form.Label>{t("incident.status")}</Form.Label>
                    <Select2<string>
                      name="statusType"
                      placeholder={t("incident.selectStatus")}
                      options={statusTypeOptions}
                      onChange={(value) => {
                        setValue("statusType", value as string);
                      }}
                      value={selectedStatusType}
                    />
                    {errors.statusType?.message && (
                      <div className="invalid-feedback d-block">
                        {t(errors.statusType.message as string)}
                      </div>
                    )}
                  </Form.Group>
                </Col>
              )}
            </Row>
          </Card.Body>
          <Card.Footer className="text-end">
            <Button
              variant="light"
              className="me-2"
              onClick={() => navigate(-1)}
            >
              {t("button.cancel")}
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? t("button.submitting") : t("button.save")}
            </Button>
          </Card.Footer>
        </Card>
      </Form>

      {/* Activity Timeline & Incident Summary - Only show in view mode */}
      {isViewMode && incident && (
        <Row className="mt-4">
          {/* Left Column: Map + Activity Timeline */}
          <Col md={8}>
            {/* Map Card */}
            <Card className="mb-3">
              <Card.Body className="p-0">
                <GoogleMapComponent
                  latitude={incident.latitude}
                  longitude={incident.longitude}
                  height="250px"
                  zoom={17}
                  markers={activityMarkers}
                  mainLabel={t("incident.incidentLocation")}
                />
              </Card.Body>
            </Card>

            {/* Activity Timeline */}
            <Card>
              <Card.Header>
                <h5>{t("incident.activityTimeline")}</h5>
              </Card.Header>
              <Card.Body style={{ maxHeight: "500px", overflowY: "auto" }}>
                <div className="d-flex flex-column gap-3">
                  {incidentDetails?.activities &&
                  incidentDetails.activities.length > 0 ? (
                    sortActivitiesNewestFirst(incidentDetails.activities).map((activity) => {
                      const { icon, imageSrc, message } = getActivityDisplay(
                        activity,
                        t,
                        incidentDetails?.incident?.incidentType,
                      );
                      const isClickable =
                        activity.mediaType === "IMAGE" ||
                        activity.mediaType === "VIDEO" ||
                        activity.mediaType === "VOICE_RECORD";
                      return (
                        <div
                          key={activity.id}
                          className={`d-flex align-items-start gap-3 p-3 rounded ${
                            newActivityIds.has(activity.id)
                              ? "activity-item--new"
                              : "bg-light"
                          }`}
                        >
                          <div
                            className={`avtar avtar-m flex-shrink-0 ${
                              imageSrc ? "activity-icon--incident" : "bg-light-secondary"
                            }`}
                            style={
                              isClickable ? { cursor: "pointer" } : undefined
                            }
                            onClick={
                              isClickable
                                ? () => handleOpenContent(activity)
                                : undefined
                            }
                            role={isClickable ? "button" : undefined}
                            title={
                              isClickable
                                ? t("incident.clickToView")
                                : undefined
                            }
                          >
                            <ActivityIcon icon={icon} imageSrc={imageSrc} />
                          </div>
                          <div
                            className="flex-grow-1"
                            style={
                              isClickable ? { cursor: "pointer" } : undefined
                            }
                            onClick={
                              isClickable
                                ? () => handleOpenContent(activity)
                                : undefined
                            }
                            role={isClickable ? "button" : undefined}
                          >
                            <p className="mb-0">
                              <small className="text-muted">
                                {formatActivityDate(activity.createdAt)}
                              </small>
                            </p>
                            <p className="mb-0">
                              <strong>{activity.userFullName}</strong> {message}
                            </p>
                            {isClickable && (
                              <small className="text-primary mt-1 d-block">
                                <i className="ti ti-eye me-1"></i>
                                {t("incident.clickToView")}
                              </small>
                            )}
                          </div>
                          <Button
                            variant="light"
                            size="sm"
                            className="flex-shrink-0 text-danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedActivity(activity);
                              setShowDeleteActivityModal(true);
                            }}
                            title={t("incident.deleteActivity")}
                          >
                            <i className="ti ti-trash f-16"></i>
                          </Button>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-4">
                      <i className="ti ti-list f-40 text-muted mb-2"></i>
                      <p className="text-muted">
                        {t("incident.noActivityYet")}
                      </p>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Right Column: Incident Summary */}
          <Col md={4}>
            <Card
              className={
                watch("drillMode") ? "border-warning" : "border-danger"
              }
              style={{ borderWidth: watch("drillMode") ? "2px" : "3px" }}
            >
              <Card.Header
                className={
                  watch("drillMode")
                    ? "bg-warning bg-opacity-25"
                    : "bg-danger bg-opacity-25"
                }
              >
                <h5
                  className={
                    watch("drillMode")
                      ? "mb-0 text-warning-emphasis"
                      : "mb-0 text-danger-emphasis"
                  }
                >
                  {t(`incident.type.${watch("incidentType")}`)}{" "}
                  {t("incident.isInProgress")}
                </h5>
              </Card.Header>
              <Card.Body
                className={
                  watch("drillMode")
                    ? "bg-warning bg-opacity-10"
                    : "bg-danger bg-opacity-10"
                }
              >
                <Row>
                  <Col xs={7}>
                    <div className="mb-3">
                      <h6 className="mb-2">{t("incident.reportedBy")}</h6>
                      <p className="mb-0">
                        {incidentDetails?.reportedBy ||
                          incident.userFullName ||
                          t("general.notAvailable")}
                      </p>
                    </div>

                    <div className="mb-3">
                      <h6 className="mb-2">{t("general.location")}</h6>
                      <p className="mb-0">
                        {incidentDetails?.location ||
                          incident.institutionName ||
                          t("general.notAvailable")}
                      </p>
                    </div>

                    {incidentDetails?.address && (
                      <div className="mb-3">
                        <h6 className="mb-2">{t("general.address")}</h6>
                        <p className="mb-0">{incidentDetails.address}</p>
                      </div>
                    )}

                    <div className="mb-0">
                      <h6 className="mb-2">{t("incident.reportedDate")}</h6>
                      <p className="mb-0">
                        {incidentDetails?.reportedAt
                          ? new Date(
                              incidentDetails.reportedAt,
                            ).toLocaleString()
                          : incident.createdAt
                            ? new Date(incident.createdAt).toLocaleString()
                            : t("general.notAvailable")}
                      </p>
                    </div>
                  </Col>

                  <Col
                    xs={5}
                    className="d-flex align-items-center justify-content-center"
                  >
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle"
                      style={{
                        width: "120px",
                        height: "120px",
                        backgroundColor: "#d21034",
                      }}
                    >
                      <img
                        src={`/assets/images/${watch("incidentType").toLowerCase()}.png`}
                        alt={t(`incident.type.${watch("incidentType")}`)}
                        style={{
                          width: "70px",
                          height: "70px",
                          objectFit: "contain",
                        }}
                      />
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Delete Incident Modal */}
      <DeleteModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        handleDeleteId={handleDeleteIncident}
        name={incident ? t(`incident.type.${incident.incidentType}`) : ""}
      />

      {/* Delete Activity Modal */}
      <DeleteModal
        show={showDeleteActivityModal}
        handleClose={() => {
          setShowDeleteActivityModal(false);
          setSelectedActivity(undefined);
        }}
        handleDeleteId={handleDeleteActivity}
        name={selectedActivity?.userFullName}
      />

      {/* Content Viewer Modal */}
      <Modal
        show={contentModal.show}
        onHide={handleCloseContent}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {contentModal.mediaType === "IMAGE"
              ? t("incident.imagePreview")
              : contentModal.mediaType === "VIDEO"
                ? t("incident.videoPreview")
                : t("incident.audioPreview")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-0">
          {contentModal.loading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : contentModal.url ? (
            contentModal.mediaType === "VIDEO" ? (
              <video
                src={contentModal.url}
                controls
                autoPlay
                style={{ width: "100%", maxHeight: "70vh" }}
              />
            ) : contentModal.mediaType === "VOICE_RECORD" ? (
              <div className="p-4">
                <div className="d-flex flex-column align-items-center gap-3">
                  <div className="avtar avtar-l bg-light-primary rounded-circle">
                    <i className="ti ti-volume f-36"></i>
                  </div>
                  <audio
                    src={contentModal.url}
                    controls
                    autoPlay
                    style={{ width: "100%" }}
                  />
                </div>
              </div>
            ) : (
              <img
                src={contentModal.url}
                alt={t("incident.imagePreview")}
                style={{
                  width: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                }}
              />
            )
          ) : null}
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

export default OperatorIncidentForm;
